import { inject } from '@angular/core';
import { PAGINATION_DEFAULT } from '@core/constants';
import { ShipmentFileUploading, UploadingStatus } from '@core/models';
import {
  ShipmentDetailsModel,
  ShipmentsFilter,
  ShipmentsResponseModel,
} from '@core/models/shipment.model';
import { SettingsService } from '@core/services/setting.service';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, Subject, switchMap, takeUntil, tap } from 'rxjs';

type ShipmentState = {
  isLoading: boolean;

  shipmentFileUpload: ShipmentFileUploading[] | undefined;
  shipmentData: ShipmentsResponseModel | undefined;
  shipmentFilter: ShipmentsFilter;
  isLoadingShipments: boolean;
  isLoadingShipmentDetails: boolean;
  isLoadingShipmentContent: boolean;
  shipmentDetails: ShipmentDetailsModel | undefined;
  shipmentContent: any;
  getShipmentDetailsError: any;
  getShipmentContentError: any;
};

const initialState: ShipmentState = {
  isLoading: false,
  isLoadingShipmentContent: false,
  isLoadingShipments: false,

  shipmentData: undefined,
  shipmentFilter: {
    limit: PAGINATION_DEFAULT.limit,
    offset: PAGINATION_DEFAULT.offset,
  },

  shipmentFileUpload: undefined,
  isLoadingShipmentDetails: false,
  shipmentDetails: undefined,
  shipmentContent: undefined,
  getShipmentContentError: null,
  getShipmentDetailsError: null,
};

export const ShipmentStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods((store, settingsService = inject(SettingsService)) => ({
    getShipments: rxMethod<number>(
      pipe(
        tap(() => patchState(store, { isLoadingShipments: true })),
        switchMap(pageIndex => {
          const shipmentFilter: ShipmentsFilter = {
            ...store.shipmentFilter(),
            offset: pageIndex,
          };

          patchState(store, { shipmentFilter });
          return settingsService.getShipments(shipmentFilter.limit, shipmentFilter.offset).pipe(
            tapResponse({
              next: (data: ShipmentsResponseModel) => {
                patchState(store, {
                  shipmentData: data,
                  shipmentFilter,
                  isLoadingShipments: false,
                });
              },
              error: () => {
                patchState(store, { isLoadingShipments: false });
              },
            }),
          );
        }),
      ),
    ),
    getShipmentContent: rxMethod<string>(
      pipe(
        tap(() =>
          patchState(store, {
            shipmentContent: undefined,
            isLoadingShipmentContent: true,
            getShipmentContentError: null,
          }),
        ),
        switchMap(shipmentId => {
          return settingsService.getShipmentContent(shipmentId).pipe(
            tapResponse({
              next: (data: any) => {
                patchState(store, {
                  shipmentContent: data,
                  isLoadingShipmentContent: false,
                });
              },
              error: () => {
                patchState(store, {
                  isLoadingShipmentContent: false,
                  getShipmentContentError: true,
                });
              },
            }),
          );
        }),
      ),
    ),
    getShipmentDetails: rxMethod<string>(
      pipe(
        tap(() =>
          patchState(store, {
            shipmentDetails: undefined,
            isLoadingShipmentDetails: true,
            getShipmentDetailsError: null,
          }),
        ),
        switchMap(shipmentId => {
          return settingsService.getShipmentDetails(shipmentId).pipe(
            tapResponse({
              next: (data: ShipmentDetailsModel) => {
                patchState(store, {
                  shipmentDetails: data,
                  isLoadingShipmentDetails: false,
                });
              },
              error: () => {
                patchState(store, {
                  isLoadingShipmentDetails: false,
                  getShipmentDetailsError: true,
                });
              },
            }),
          );
        }),
      ),
    ),

    uploadShipmentsMethod: rxMethod<File[]>(
      pipe(
        tap(files => {
          const shipmentFileUpload: ShipmentFileUploading[] = files.map((file, index) => ({
            index,
            file,
            fileName: file.name,
            status: UploadingStatus.Loading,
            cancel: new Subject<void>(),
          }));
          patchState(store, { shipmentFileUpload });
        }),
        switchMap(files => {
          return files.map((file, index) => {
            const shipmentSelected = store.shipmentFileUpload()![index];
            return settingsService
              .uploadShipments(file)
              .pipe(
                tapResponse({
                  next: () => {
                    shipmentSelected.status = UploadingStatus.Successful;
                  },
                  error: () => {
                    shipmentSelected.status = UploadingStatus.Failed;
                  },
                }),
                takeUntil(shipmentSelected.cancel),
              )
              .subscribe();
          });
        }),
      ),
    ),

    cancelShipmentUploadingMethod: rxMethod<number>(
      pipe(
        tap(index => {
          const shipmentSelected = store.shipmentFileUpload()![index];
          shipmentSelected.cancel.next();
          shipmentSelected.cancel.complete();
          shipmentSelected.status = UploadingStatus.Failed;
        }),
      ),
    ),

    refreshShipmentUploadingMethod: rxMethod<number>(
      pipe(
        tap(index => {
          const shipmentFileUpload = store.shipmentFileUpload()!.map(f => {
            if (f.index !== index) {
              return f;
            }
            return {
              ...f,
              status: UploadingStatus.Loading,
              cancel: new Subject<void>(),
            };
          });
          patchState(store, { shipmentFileUpload });
        }),
        switchMap(index => {
          const shipmentSelected = store.shipmentFileUpload()![index];
          return settingsService.uploadShipments(shipmentSelected.file).pipe(
            tapResponse({
              next: () => {
                shipmentSelected.status = UploadingStatus.Successful;
              },
              error: () => {
                shipmentSelected.status = UploadingStatus.Failed;
              },
            }),
            takeUntil(shipmentSelected.cancel),
          );
        }),
      ),
    ),
  })),
);
